import { CHORD_TYPES, type ChordTypeId } from './chords';
import { pitchClassOf } from './notes';
import { STRING_LAYOUTS, frettedMidi, type StringLayoutId } from './tuning';
import { gripFromFrets, type ChordVoicing } from './voicing';
import type { DropVoicingFamily } from './voicingFamilies';

export type AlternativeStringLayout = Exclude<StringLayoutId, 'joe-pass'>;

/** A reusable physical grip, kept separately from the strict family examples. */
export interface AlternativeShape {
  id: string;
  name: string;
  root: string;
  chordType: ChordTypeId;
  frets: (number | null)[];
  stringLayout: AlternativeStringLayout;
  /** Fixed preserves open strings and the original position; movable shifts every note. */
  transposition: 'movable' | 'fixed';
  /** Only known constructions carry a family label; a string layout alone proves none. */
  construction?: DropVoicingFamily;
}

export const MAX_ALTERNATIVE_SHAPES = 256;

export const BUILTIN_ALTERNATIVE_SHAPES: readonly AlternativeShape[] = [
  {
    id: 'rooted-13',
    name: 'Rooted 13',
    root: 'G',
    chordType: 'dom13',
    frets: [3, null, 3, 4, 5, null],
    stringLayout: 'edgb',
    transposition: 'movable',
    construction: 'drop-3',
  },
];

export const DEFAULT_PREFERRED_ALTERNATIVE_IDS: readonly string[] = ['rooted-13'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isAlternativeLayout(value: unknown): value is AlternativeStringLayout {
  return value === 'adgb' || value === 'dgbe' || value === 'edgb' || value === 'agbe';
}

/** Validate sound as well as storage shape: a label must not change the actual harmony. */
export function validateAlternativeShape(value: unknown): string | null {
  if (!isRecord(value)) return 'Choose a chord shape to save.';
  if (typeof value.id !== 'string' || !/^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,119}$/.test(value.id)) {
    return 'The shape needs a valid identifier.';
  }
  if (
    typeof value.name !== 'string' ||
    value.name.trim().length === 0 ||
    value.name.trim().length > 80
  ) {
    return 'Give the shape a name of up to 80 characters.';
  }
  if (typeof value.root !== 'string' || !/^[A-Ga-g][#b♯♭]{0,2}$/.test(value.root.trim())) {
    return 'Choose a valid chord root.';
  }
  if (
    typeof value.chordType !== 'string' ||
    !Object.prototype.hasOwnProperty.call(CHORD_TYPES, value.chordType)
  ) {
    return 'Choose a recognised chord type.';
  }
  if (!isAlternativeLayout(value.stringLayout)) return 'Choose one of the four string layouts.';
  if (value.transposition !== 'fixed' && value.transposition !== 'movable') {
    return 'Choose whether this shape can move to other keys.';
  }
  if (
    value.construction !== undefined &&
    value.construction !== 'drop-2' &&
    value.construction !== 'drop-3'
  ) {
    return 'The shape has an unrecognised construction.';
  }
  const layout = STRING_LAYOUTS[value.stringLayout];
  if (value.construction !== undefined && value.construction !== layout.family) {
    return 'The construction does not match the string layout.';
  }
  if (!Array.isArray(value.frets) || value.frets.length !== 6) {
    return 'Choose one fret for each of the four active strings.';
  }
  for (let string = 0; string < 6; string++) {
    const fret: unknown = value.frets[string];
    if (!layout.strings.includes(string)) {
      if (fret !== null) return 'Only the four strings in this layout can sound.';
    } else if (typeof fret !== 'number' || !Number.isInteger(fret) || fret < 0 || fret > 20) {
      return 'Choose a fret from 0 to 20 for each active string.';
    }
  }
  const frets = value.frets as (number | null)[];
  const midis = layout.strings.map((string) => frettedMidi(string, frets[string]!));
  if (midis.some((midi, i) => i > 0 && midi <= midis[i - 1])) {
    return 'Keep the notes ascending from the bass string to the top string.';
  }
  const chord = CHORD_TYPES[value.chordType as ChordTypeId];
  const rootPc = pitchClassOf(value.root);
  const voicedOffsets = new Set(midis.map((midi) => (midi - rootPc + 12) % 12));
  const formula = new Set(chord.intervals.map((interval) => interval % 12));
  const permitted = new Set([0, ...formula]);
  if ([...voicedOffsets].some((offset) => !permitted.has(offset))) {
    return 'This shape contains notes outside the selected chord.';
  }
  // A triad needs all three tones. A richer chord may omit root or perfect
  // fifth, but its guide tones, altered fifth and named extensions must stay.
  const required = [...formula].filter(
    (offset) => chord.category === 'triad' || (offset !== 0 && offset !== 7),
  );
  if (required.some((offset) => !voicedOffsets.has(offset))) {
    return 'Keep the notes that identify this chord, including its named extensions.';
  }
  return null;
}

/** Read persisted user data defensively and copy arrays away from their source. */
export function normalizeAlternativeShapes(value: unknown): AlternativeShape[] {
  if (!Array.isArray(value)) return [];
  const shapes: AlternativeShape[] = [];
  const ids = new Set<string>();
  for (const entry of value) {
    if (shapes.length >= MAX_ALTERNATIVE_SHAPES) break;
    if (validateAlternativeShape(entry) !== null) continue;
    const shape = entry as AlternativeShape;
    if (ids.has(shape.id)) continue;
    ids.add(shape.id);
    const root = shape.root.trim();
    shapes.push({
      id: shape.id,
      name: shape.name.trim(),
      root: root[0].toUpperCase() + root.slice(1).replaceAll('♯', '#').replaceAll('♭', 'b'),
      chordType: shape.chordType,
      frets: [...shape.frets],
      stringLayout: shape.stringLayout,
      transposition: shape.transposition,
      ...(shape.construction ? { construction: shape.construction } : {}),
    });
  }
  return shapes;
}

/** Built-ins cannot be replaced accidentally by an imported user identifier. */
export function allAlternativeShapes(custom: readonly AlternativeShape[]): AlternativeShape[] {
  const builtins = new Set(BUILTIN_ALTERNATIVE_SHAPES.map((shape) => shape.id));
  return [
    ...BUILTIN_ALTERNATIVE_SHAPES,
    ...normalizeAlternativeShapes(custom).filter((shape) => !builtins.has(shape.id)),
  ];
}

/** Unknown preference ids never grant a preference to an unrelated later entry. */
export function normalizePreferredAlternativeIds(
  value: unknown,
  shapes: readonly AlternativeShape[],
): string[] {
  const known = new Set(shapes.map((shape) => shape.id));
  const requested = Array.isArray(value) ? value : DEFAULT_PREFERRED_ALTERNATIVE_IDS;
  return [
    ...new Set(requested.filter((id): id is string => typeof id === 'string' && known.has(id))),
  ];
}

/** Transpose an entire grip, never its voices separately or into another harmony. */
export function enumerateAlternativeShapes(
  rootDisplay: string,
  chordType: ChordTypeId,
  shapes: readonly AlternativeShape[],
  spellingRoot = rootDisplay,
): ChordVoicing[] {
  const rootPc = pitchClassOf(rootDisplay);
  const result: ChordVoicing[] = [];
  for (const shape of shapes) {
    if (shape.chordType !== chordType || validateAlternativeShape(shape) !== null) continue;
    const sourceRootPc = pitchClassOf(shape.root);
    const occupied = shape.frets.filter((fret): fret is number => fret !== null);
    const lowestShift = shape.transposition === 'fixed' ? 0 : -Math.min(...occupied);
    const highestShift = shape.transposition === 'fixed' ? 0 : 20 - Math.max(...occupied);
    for (let shift = lowestShift; shift <= highestShift; shift++) {
      if ((((sourceRootPc + shift) % 12) + 12) % 12 !== rootPc) continue;
      const frets = shape.frets.map((fret) => (fret === null ? null : fret + shift));
      const grip = gripFromFrets(spellingRoot, chordType, frets, shape.stringLayout);
      if (!grip) continue;
      result.push({
        ...grip,
        alternativeShape: {
          id: shape.id,
          name: shape.name,
          ...(shape.construction ? { construction: shape.construction } : {}),
        },
      });
    }
  }
  return result;
}
